/**
 * Shared hook for library file-tree operations.
 *
 * Unifies create-folder, delete, rename, move, file-drop, context-menu,
 * and expand/collapse across all sampler editors that use PluginLibraryBrowser.
 *
 * Device-specific behavior is injected via LibraryOperationsStrategy.
 */
import { useCallback, useState } from 'react';
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import { createFolder, deleteItem, moveItem, importWavToCommonArea, getNestedDirectory, moveDirectory, sanitizeForFilename, } from '@audiocontrol/sampler-library/browser';
export const SAVE_DIALOG_CLOSED = {
    open: false, itemIndex: 0, itemName: '',
};
export const SEND_DIALOG_CLOSED = {
    open: false, itemName: '', itemPath: [],
};
/**
 * Create a handleContextMenuAction function from declared transfer capabilities.
 *
 * The handlers parameter uses Required<Pick<...>> so the compiler enforces
 * that every declared action has a corresponding handler.
 */
export function createTransferActionHandler(handlers, 
/** Category IDs that contain device-specific programs (e.g., 's3k-programs'). */
deviceProgramCategories = []) {
    const handlerMap = handlers;
    return (categoryId, actionId, node) => {
        const meta = (node.meta ?? {});
        const name = node.name;
        switch (actionId) {
            case 'send-sample-to-device': {
                if (!handlerMap['send-sample-to-device'])
                    return { handled: false };
                const path = meta.path ?? [];
                handlerMap['send-sample-to-device'](name, path);
                return { handled: true };
            }
            case 'import-drum-kit': {
                if (!handlerMap['import-drum-kit'])
                    return { handled: false };
                const path = meta.path ?? [];
                handlerMap['import-drum-kit'](name, path);
                return { handled: true };
            }
            case 'edit-drum-kit': {
                if (!handlerMap['edit-drum-kit'])
                    return { handled: false };
                const path = meta.path ?? [];
                handlerMap['edit-drum-kit'](name, node.type, path);
                return { handled: true };
            }
            case 'send-program-to-device': {
                if (!handlerMap['send-program-to-device'])
                    return { handled: false };
                if (!deviceProgramCategories.includes(categoryId))
                    return { handled: false };
                const dirName = meta.dirName ?? name;
                handlerMap['send-program-to-device'](dirName, name);
                return { handled: true };
            }
            case 'promote-to-common-area': {
                if (!handlerMap['promote-to-common-area'])
                    return { handled: false };
                if (!deviceProgramCategories.includes(categoryId))
                    return { handled: false };
                const dirName = meta.dirName ?? name;
                handlerMap['promote-to-common-area'](dirName);
                return { handled: true };
            }
            case 'import-instrument': {
                if (!handlerMap['import-instrument'])
                    return { handled: false };
                if (deviceProgramCategories.includes(categoryId))
                    return { handled: false };
                const dirName = meta.directoryName ?? name;
                const path = meta.path ?? [];
                // Programs in 'programs' or 'common-programs' categories live in the programs root
                const fromProgramsDir = categoryId === 'programs' || categoryId === 'common-programs';
                handlerMap['import-instrument'](dirName, path, fromProgramsDir);
                return { handled: true };
            }
            default:
                return { handled: false };
        }
    };
}
// =========================================================================
// Helpers
// =========================================================================
export function getNodeName(node) {
    const meta = node.meta;
    if (!meta)
        return node.name;
    const candidate = meta['fileName'] ?? meta['directoryName'] ?? meta['dirName'];
    if (typeof candidate === 'string')
        return candidate;
    return node.name;
}
export function getNodePath(node) {
    const meta = node.meta;
    if (!meta)
        return [];
    const path = meta['path'];
    if (Array.isArray(path) && path.every((p) => typeof p === 'string')) {
        return path;
    }
    return [];
}
/** Programs categories use a different root directory than samples. */
const PROGRAM_CATEGORIES = new Set(['programs', 'common-programs']);
const PROGRAMS_ROOT = ['library', 'common', 'programs'];
/** Get the root directory for a category's filesystem operations. */
async function getCategoryDir(root, categoryId, subPath) {
    if (PROGRAM_CATEGORIES.has(categoryId)) {
        return getNestedDirectory(root, [...PROGRAMS_ROOT, ...subPath]);
    }
    // Default: samples root (handled by the sampler-library createFolder/moveItem/deleteItem)
    throw new Error('USE_DEFAULT');
}
// =========================================================================
// Hook
// =========================================================================
export function useLibraryOperations(libraryRoot, strategy, onRefresh, errorReporter, onEditorAction) {
    const onError = errorReporter.report;
    const [expandedPaths, setExpandedPaths] = useState({});
    const onToggleExpand = useCallback((categoryId, nodeId) => {
        setExpandedPaths((prev) => {
            const categorySet = new Set(prev[categoryId]);
            if (categorySet.has(nodeId)) {
                categorySet.delete(nodeId);
            }
            else {
                categorySet.add(nodeId);
            }
            return { ...prev, [categoryId]: categorySet };
        });
    }, []);
    const onCreateFolder = useCallback(async (categoryId, parentPath, name) => {
        if (!libraryRoot) {
            onError('Library is not connected');
            return;
        }
        const folderName = name?.trim();
        if (!folderName)
            return;
        try {
            const result = await strategy?.createFolder?.(categoryId, parentPath, folderName);
            if (!result?.handled) {
                if (PROGRAM_CATEGORIES.has(categoryId)) {
                    const dir = await getCategoryDir(libraryRoot, categoryId, parentPath);
                    await dir.getDirectoryHandle(folderName, { create: true });
                }
                else {
                    await createFolder(libraryRoot, parentPath, folderName);
                }
            }
            onRefresh();
        }
        catch (err) {
            onError(`Failed to create folder: ${err instanceof Error ? err.message : String(err)}`);
        }
    }, [libraryRoot, strategy, onRefresh, onError]);
    const onDelete = useCallback(async (categoryId, node) => {
        if (!libraryRoot) {
            onError('Library is not connected');
            return;
        }
        const name = getNodeName(node);
        try {
            if (strategy?.deleteItem) {
                const result = await strategy.deleteItem(categoryId, node);
                if (result.handled) {
                    onRefresh();
                    return;
                }
            }
            const path = getNodePath(node);
            if (PROGRAM_CATEGORIES.has(categoryId)) {
                const dir = await getCategoryDir(libraryRoot, categoryId, path);
                await dir.removeEntry(name, { recursive: true });
            }
            else {
                await deleteItem(libraryRoot, name, path);
            }
            onRefresh();
        }
        catch (err) {
            onError(`Failed to delete "${name}": ${err instanceof Error ? err.message : String(err)}`);
        }
    }, [libraryRoot, strategy, onRefresh, onError]);
    const onMove = useCallback(async (categoryId, node, targetPath) => {
        if (!libraryRoot) {
            onError('Library is not connected');
            return;
        }
        const name = getNodeName(node);
        const sourcePath = getNodePath(node);
        try {
            // Strategy first: device-specific categories (Roland's
            // library/<device>/<category>/, Akai's program tree) own the
            // path layout and must handle the move themselves. The
            // common-area `moveItem` only knows `library/common/samples/`
            // and will throw "not found" against any other root.
            if (strategy?.moveItem) {
                const result = await strategy.moveItem(categoryId, node, targetPath);
                if (result.handled) {
                    onRefresh();
                    return;
                }
            }
            if (PROGRAM_CATEGORIES.has(categoryId)) {
                const srcParent = await getCategoryDir(libraryRoot, categoryId, sourcePath);
                const destParent = await getCategoryDir(libraryRoot, categoryId, targetPath);
                await moveDirectory(srcParent, name, destParent);
            }
            else {
                await moveItem(libraryRoot, name, sourcePath, targetPath);
            }
            onRefresh();
        }
        catch (err) {
            onError(`Failed to move "${name}": ${err instanceof Error ? err.message : String(err)}`);
        }
    }, [libraryRoot, strategy, onRefresh, onError]);
    const onRename = useCallback(async (categoryId, node, newName) => {
        if (!libraryRoot) {
            onError('Library is not connected');
            return;
        }
        const oldDirName = getNodeName(node);
        const path = getNodePath(node);
        try {
            const result = await strategy?.renameItem(categoryId, node, newName);
            if (result?.handled) {
                onRefresh();
                return;
            }
            // Common-area fallback: rename directory bundle
            // 1. Resolve the parent directory and old item directory
            const isProgram = PROGRAM_CATEGORIES.has(categoryId);
            const parentDir = isProgram
                ? await getCategoryDir(libraryRoot, categoryId, path)
                : await getNestedDirectory(libraryRoot, ['library', 'common', 'samples', ...path]);
            const oldDir = await parentDir.getDirectoryHandle(oldDirName);
            // 2. Update the name field inside the YAML metadata
            const yamlFilename = isProgram ? 'program.yaml' : 'sample.yaml';
            const yamlHandle = await oldDir.getFileHandle(yamlFilename);
            const yamlFile = await yamlHandle.getFile();
            const yamlText = await yamlFile.text();
            const parsed = parseYaml(yamlText);
            parsed.name = newName;
            const writable = await yamlHandle.createWritable();
            await writable.write(stringifyYaml(parsed, { indent: 2, lineWidth: 120 }));
            await writable.close();
            // 3. Rename the directory (copy to new name, delete old)
            const safeName = sanitizeForFilename(newName);
            if (safeName !== oldDirName) {
                await moveDirectory(parentDir, oldDirName, parentDir, safeName);
            }
            onRefresh();
        }
        catch (err) {
            onError(`Failed to rename "${node.name}": ${err instanceof Error ? err.message : String(err)}`);
        }
    }, [libraryRoot, strategy, onRefresh, onError]);
    const onFileDrop = useCallback(async (categoryId, files, targetPath) => {
        if (!libraryRoot) {
            onError('Library is not connected');
            return;
        }
        try {
            for (const file of files) {
                const buffer = await file.arrayBuffer();
                const data = new Uint8Array(buffer);
                await importWavToCommonArea(libraryRoot, file.name, data, { targetPath });
            }
            onRefresh();
        }
        catch (err) {
            onError(`Failed to import files: ${err instanceof Error ? err.message : String(err)}`);
        }
    }, [libraryRoot, onRefresh, onError]);
    const onContextMenuAction = useCallback((categoryId, actionId, node) => {
        // Let strategy handle transfer actions first
        if (strategy) {
            const result = strategy.handleContextMenuAction(categoryId, actionId, node);
            if (result.handled)
                return;
        }
        // Editor actions -- delegate to consumer callback
        const editorActions = ['open-loop-editor', 'open-chopper', 'open-sample-editor'];
        if (editorActions.includes(actionId)) {
            if (!onEditorAction) {
                throw new Error(`Editor action "${actionId}" has no handler -- onEditorAction callback is missing`);
            }
            const name = getNodeName(node);
            const path = getNodePath(node);
            onEditorAction(actionId, name, node.type, path.length > 0 ? path : undefined);
            return;
        }
        // 'move' is intercepted by PluginLibraryBrowser before reaching here.
        // If it arrives, the component didn't handle it -- that's a bug.
        if (actionId === 'move') {
            throw new Error('Move action should be handled by PluginLibraryBrowser, not useLibraryOperations');
        }
        // If we reach here, no one handled the action -- this is a bug
        throw new Error(`Unhandled context menu action "${actionId}" for category "${categoryId}", ` +
            `node type "${node.type}". Either the action should not appear in the menu, ` +
            `or a handler is missing.`);
    }, [strategy, onEditorAction]);
    const onBatchDelete = useCallback(async (categoryId, nodes) => {
        if (!libraryRoot) {
            onError('Library is not connected');
            return;
        }
        const errors = [];
        for (const node of nodes) {
            try {
                const name = getNodeName(node);
                if (strategy?.deleteItem) {
                    const result = await strategy.deleteItem(categoryId, node);
                    if (result.handled)
                        continue;
                }
                const path = getNodePath(node);
                if (PROGRAM_CATEGORIES.has(categoryId)) {
                    const dir = await getCategoryDir(libraryRoot, categoryId, path);
                    await dir.removeEntry(name, { recursive: true });
                }
                else {
                    await deleteItem(libraryRoot, name, path);
                }
            }
            catch (err) {
                errors.push(`${node.name}: ${err instanceof Error ? err.message : String(err)}`);
            }
        }
        onRefresh();
        if (errors.length > 0) {
            onError(`Failed to delete ${errors.length} item(s):\n${errors.join('\n')}`);
        }
    }, [libraryRoot, strategy, onRefresh, onError]);
    const onBatchMove = useCallback(async (categoryId, nodes, targetPath) => {
        if (!libraryRoot) {
            onError('Library is not connected');
            return;
        }
        const errors = [];
        for (const node of nodes) {
            try {
                // Same strategy-first routing as the single-item `onMove`.
                // Without this, device-specific categories whose files don't
                // live in `library/common/samples/` fall through to the
                // common-area `moveItem` per-item and emit one
                // "file or directory not found" per node.
                if (strategy?.moveItem) {
                    const result = await strategy.moveItem(categoryId, node, targetPath);
                    if (result.handled)
                        continue;
                }
                const name = getNodeName(node);
                const sourcePath = getNodePath(node);
                if (PROGRAM_CATEGORIES.has(categoryId)) {
                    const srcParent = await getCategoryDir(libraryRoot, categoryId, sourcePath);
                    const destParent = await getCategoryDir(libraryRoot, categoryId, targetPath);
                    await moveDirectory(srcParent, name, destParent);
                }
                else {
                    await moveItem(libraryRoot, name, sourcePath, targetPath);
                }
            }
            catch (err) {
                errors.push(`${node.name}: ${err instanceof Error ? err.message : String(err)}`);
            }
        }
        onRefresh();
        if (errors.length > 0) {
            onError(`Failed to move ${errors.length} item(s):\n${errors.join('\n')}`);
        }
    }, [libraryRoot, strategy, onRefresh, onError]);
    return {
        expandedPaths,
        onToggleExpand,
        onCreateFolder,
        onDelete,
        onMove,
        onRename,
        onFileDrop,
        onContextMenuAction,
        onBatchDelete,
        onBatchMove,
    };
}
//# sourceMappingURL=useLibraryOperations.js.map